import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import TitleSection from "src/components/TitleSection/TitleSection";
import Button from "src/components/Button/Button";
import Alert from 'react-bootstrap/Alert';
import ReactMarkdown from "react-markdown";

/**
 * SubscriptionDisplay component - A subscription-specific component for displaying
 * subscription content within a dark container. This component handles only the content
 * and should be wrapped in a background container (like StatusBackground) by the parent.
 *
 * @param {boolean} loading - Whether the component is in a loading state
 * @param {string} error - Error message to display (if any)
 * @param {string} email - User's email address
 * @param {boolean} subscribed - Whether the user is currently subscribed
 * @param {boolean} updating - Whether the subscription is currently being updated
 * @param {function} onToggle - Function to call when button is clicked
 */
const SubscriptionDisplay = ({
  loading = false,
  error = null,
  email = "",
  subscribed = false,
  updating = false,
  onToggle = () => {}
}) => {
  const { t } = useTranslation();

  return (
    <Centered>
      <TitleSection
        title={t('subscription.title')}
        text={t('subscription.description')}
        className="pb-2"
      />

      {loading && (
          <Alert variant="light">
            <EmailStatusIcon subscribed={subscribed}>
              <FontAwesomeIcon icon={faEnvelope} className="email-icon" />
            </EmailStatusIcon>
            <p>{t('subscription.loading')}</p>
          </Alert>
      )}

      {error && (
        <Alert variant="warning">
          {t('error.oops')}: {error}
        </Alert>
      )}

      {!loading && !error && (
        <>
          <Alert variant="light">
            <EmailStatusIcon subscribed={subscribed}>
              <FontAwesomeIcon icon={faEnvelope} className="email-icon" />
              <FontAwesomeIcon
                data-cy={`subscribed-${subscribed}-icon`}
                icon={subscribed ? faCheck : faXmark}
                className="status-icon"
              />
            </EmailStatusIcon>
            <p>{email}</p>
            <p><strong>
              {t('subscription.status.label')}{subscribed ? t('subscription.status.subscribed') : t('subscription.status.unsubscribed')}
            </strong>
            </p>
          </Alert>
          <ReactMarkdown>{subscribed ? t('subscription.messages.subscribedMessage') : t('subscription.messages.unsubscribedMessage')}</ReactMarkdown>

          <Button
            label={updating
              ? t('subscription.buttons.updating')
              : subscribed
                ? t('subscription.buttons.unsubscribe')
                : t('subscription.buttons.subscribe')}
            onClick={onToggle}
            disabled={updating}
            bgColor={subscribed ? 'var(--red)' : 'var(--darkgreen)'}
            className="mt-4"
          />
        </>
      )}
    </Centered>
  );
};

const Centered = styled.div`
  text-align: center;
  .alert {
    &.alert-light {
      color: var(--black)
    }
    p:last-child {
    }
  }
`;

const EmailStatusIcon = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 10px;
  .email-icon {
    font-size: 30px;
    color: var(--grey);
  }

  .status-icon {
    position: absolute;
    bottom: -4px;
    right: -8px;
    font-size: 12px;
    color: white;
    border-radius: 50%;
    padding: 2px;
    background: ${props => props.subscribed ? 'var(--green)' : 'var(--red)'};
    border: 2px solid white;
  }
`;

export default SubscriptionDisplay;
